import { useReveal } from "@/hooks/useReveal";
import { UFJ_IMAGES } from "./data";

const STEPS = [
  { n: "I",   title: "Private Dialogue",     body: "An introductory conversation with a maison concierge to understand the intention of the commission." },
  { n: "II",  title: "Stone Selection",      body: "Access to the maison's private reserve of GIA-certified diamonds and curated coloured gemstones." },
  { n: "III", title: "Design Direction",     body: "The atelier composes initial studies — pencil, gouache, three-dimensional modelling — refined with the client." },
  { n: "IV",  title: "Realisation",          body: "The piece is executed by hand in the maison's workshops, with discreet milestone updates." },
  { n: "V",   title: "Private Delivery",     body: "Final presentation in Monaco, Geneva, or at a residence of the client's choosing. The piece is signed and archived." },
];

export default function UFJBespoke({ onBeginCommission }) {
  const eyebrow = useReveal();
  const title = useReveal();
  const lead = useReveal();
  const img = useReveal();
  const cta = useReveal();

  return (
    <section
      id="ufj-bespoke"
      data-testid="ufj-bespoke"
      className="relative bg-[#030303] py-28 md:py-40 overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 -right-32 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,rgba(212,175,140,0.10)_0%,transparent_70%)]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p ref={eyebrow} className="pd-reveal eyebrow" data-testid="ufj-bespoke-eyebrow">
            Bespoke High Jewelry
          </p>
          <h2
            ref={title}
            className="pd-reveal mt-6 font-serif-display text-4xl md:text-6xl lg:text-7xl text-white tracking-[0.06em] leading-[1.05]"
            style={{ transitionDelay: "160ms" }}
            data-testid="ufj-bespoke-title"
          >
            Composed privately, <br />
            <span className="italic font-serif-body text-gold-gradient">for one.</span>
          </h2>
          <div ref={lead} className="pd-reveal mt-8" style={{ transitionDelay: "320ms" }}>
            <p className="font-serif-body italic text-lg md:text-xl text-white/80 leading-relaxed">
              A commission is a dialogue — never a transaction.
            </p>
            <p className="mt-4 text-white/55 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
              Clients may inquire about personalised concepts, gemstone selection, design direction and private specifications. Each commission is confirmed only after an in-person conversation with the atelier.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div ref={img} className="pd-reveal lg:col-span-6">
            <div className="relative aspect-[4/5] overflow-hidden border border-[var(--pd-border)]">
              <img
                src={UFJ_IMAGES.bespokeAtelier}
                alt="PiérrecDavís Atelier — concept imagery"
                className="w-full h-full object-cover transition-transform duration-[2400ms] hover:scale-105"
                data-testid="ufj-bespoke-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute top-4 left-4">
                <span className="text-[0.55rem] uppercase tracking-[0.42em] text-white/55 bg-black/60 backdrop-blur px-3 py-1.5">
                  Concept Imagery
                </span>
              </div>
              <div className="absolute bottom-5 left-5">
                <p className="eyebrow text-white/70">The Atelier</p>
                <p className="font-serif-display text-2xl text-white mt-1 tracking-[0.16em]">By Hand</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6">
            <p className="eyebrow text-[var(--pd-gold)]">The Commission Process</p>
            <div className="mt-6 space-y-5">
              {STEPS.map((s, i) => (
                <div
                  key={s.n}
                  data-testid={`ufj-bespoke-step-${s.n.toLowerCase()}`}
                  className="flex gap-6 border-b border-white/10 pb-5"
                >
                  <span className="font-serif-display text-2xl text-[var(--pd-gold)] tracking-[0.1em] shrink-0 w-10">
                    {s.n}
                  </span>
                  <div>
                    <h4 className="font-serif-display text-lg text-white tracking-[0.08em]">
                      {s.title}
                    </h4>
                    <p className="mt-1.5 text-white/60 text-sm leading-relaxed">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>

            <div ref={cta} className="pd-reveal mt-10 flex flex-col sm:flex-row items-start gap-4">
              <button
                onClick={onBeginCommission}
                data-testid="ufj-begin-commission-btn"
                className="btn-gold"
              >
                <span>Begin Your Private Commission</span>
              </button>
              <p className="text-white/40 text-[0.65rem] uppercase tracking-[0.36em] max-w-xs leading-relaxed pt-4">
                All consultations held in confidence · Timing confirmed on request
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
